<template>
  <div v-if="workflow" class="align-center d-flex justify-space-between ma-2 mr-n4 flex-nowrap top-workflow"
       style="gap: 8px;">
    <v-tooltip location="bottom" v-if="workflowStatusCompleted" width="400">
      <template v-slot:activator="{ props }">
        <v-btn class="mr-3" v-bind="props" variant="outlined" color="red" :ripple="false" prepend-icon="mds:lock">
          This workflow is locked
        </v-btn>
      </template>

      <span
      >
        This workflow is marked as completed.
        <span v-if="subscriptionAccessType=== 'FULL'">To unlock this workflow click the settings button to the right and go to general settings.</span>
        <span v-else>To unlock this workflow, please upgrade your subscription or contact support for further assistance.</span>
      </span
      >
    </v-tooltip>
    <span v-else style="flex: 1;"></span>

    <div class="d-flex justify-center flex-nowrap" style="flex: 1;">
      <div class="btn-group">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mdi-hand-back-right-outline"
              size="default"
              class="mr-2"
              :class="{ 'hasSelected': $store.state.interactionMode === 'hand' }"
              @click="$store.commit('setInteractionMode', 'hand')"
            />
          </template>
          <span>Hand tool: Drag canvas (H)</span>
        </v-tooltip>

        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mdi-selection-drag"
              size="default"
              class="mr-2"
              :class="{ 'hasSelected': $store.state.interactionMode === 'pointer' }"
              :disabled="workflowStatusCompleted || $store.state.workflow.isViewOnlyEnabled"
              @click="$store.commit('setInteractionMode', 'pointer')"
            />
          </template>
          <span>Pointer tool: Select shapes (V)</span>
        </v-tooltip>

        <template v-if="!$store.state.workflow.isViewOnlyEnabled && subscriptionAccessType === 'FULL'">
          <v-tooltip location="top" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :disabled="!previousVersionExists || workflowStatusCompleted"
                variant="tonal"
                icon="mds:replay"
                size="default"
                class="mr-2 captures-description"
                @click="undoDebounced(workflow)"
              />
            </template>
            <span
            >Undo: {{ changeNoteCurrent?.userRealName }}
              {{ changeNoteCurrent?.action }}
              <span>{{ changeNoteCurrent?.target?.substring(0, 30) }}</span>
              <span v-if="changeNoteCurrent?.target?.length > 30">...</span>
            </span>
          </v-tooltip>

          <v-tooltip location="top" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :disabled="!laterVersionExists || workflowStatusCompleted"
                variant="tonal"
                icon="mds:replay"
                class="mr-2 flip-icon-horizontal captures-description"
                @click="redoDebounced(workflow)"
              />
            </template>
            <span
            >Redo: {{ changeNoteRedo?.userRealName }}
              {{ changeNoteRedo?.action }}
              <span>{{ changeNoteRedo?.target?.substring(0, 30) }}</span>
              <span v-if="changeNoteRedo?.target?.length > 30">...</span>
            </span>
          </v-tooltip>
        </template>

        <v-menu
          v-model="filterMenuOpen"
          :close-on-content-click="false"
          :contained="!isButtonOutOfView"
          min-width="600"
          location="bottom start"
          :offset="4"
          :attach="isButtonOutOfView ? 'body' : false"
          :transition="false"
          content-class="filter-menu-content"
        >
          <template v-slot:activator="{ props: menuProps }">
            <v-tooltip
              location="top"
              open-delay="500"
              :disabled="menuProps['aria-expanded'] === 'true'"
            >
              <template v-slot:activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps" ref="filterButtonRef">
                  <v-btn
                    :disabled="!releases?.length && !tagCardType"
                    v-bind="menuProps"
                    variant="tonal"
                    minWidth="52"
                    class="px-1 mr-2"
                    :class="{hasSelected: activeFilterCount > 0}"
                  >
                    <v-icon>mds:filter-list</v-icon>
                    <span v-if="activeFilterCount > 0">({{ activeFilterCount }})</span>
                  </v-btn>
                </div>
              </template>
              <span
                v-if="!releases?.length && !tagCardType">Assign cards to releases or create Tag card type, then filter</span>
              <span v-else>Filter by releases and tags</span>
            </v-tooltip>
          </template>
          <div
            ref="filterMenuWrapper"
            class="filter-menu-wrapper"
            :class="{ 'draggable': isButtonOutOfView, 'has-header': isButtonOutOfView }"
          >
            <div v-if="isButtonOutOfView" class="filter-menu-header" @mousedown="handleDragStart">
              <div class="filter-menu-drag-handle">
                <v-icon size="small">mds:drag-indicator</v-icon>
              </div>
              <div class="filter-menu-close-btn" @click.stop="closeFilterMenu">
                <v-icon size="small">mds:close</v-icon>
              </div>
            </div>
            <div class="filter-menu-container">
              <v-list max-height="400" class="filter-column">
                <v-list-subheader>Release</v-list-subheader>
                <template v-for="(item, index) in releases" :key="index">
                  <v-list-item
                    v-memo="[showReleaseNo, showTagCardType, item.value]"
                    :class="{releaseFilter: true, 'active': showReleaseNo?.includes(item.value)}"
                    @click="toggleReleaseFilter(item)"
                  >
                    <v-list-item-title>{{ item.title }} ({{ getReleaseCount(item.value) }})</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item v-if="!releases?.length" disabled>
                  <v-list-item-title class="text-grey">No releases</v-list-item-title>
                </v-list-item>
              </v-list>
              <v-list v-if="tagCardType" max-height="400" class="filter-column">
                <v-list-subheader>{{ tagCardType.title }}</v-list-subheader>
                <template v-for="(tagValue, index) in tagCardTypeValues" :key="index">
                  <v-list-item
                    v-memo="[showReleaseNo, showTagCardType, tagValue]"
                    :class="{releaseFilter: true, 'active': showTagCardType?.includes(tagValue)}"
                    @click="toggleTagCardTypeFilter(tagValue)"
                  >
                    <v-list-item-title>{{ tagValue }} ({{ getTagCardTypeCount(tagValue) }})</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item v-if="!tagCardTypeValues.length" disabled>
                  <v-list-item-title class="text-grey">No tags</v-list-item-title>
                </v-list-item>
              </v-list>
            </div>
            <div class="filter-menu-footer">
              <div class="text-caption text-grey">
                Numbers show number of domain events.
              </div>
              <v-btn
                variant="outlined"
                size="small"
                @click="clearFilters"
                :disabled="activeFilterCount === 0"
                prepend-icon="mds:close"
              >
                Clear filter
              </v-btn>
            </div>
          </div>
        </v-menu>
        <ai-assistant-button v-if="!$store.state.workflow.isViewOnlyEnabled" />
      </div>
    </div>

    <div class="d-flex justify-end" style="flex: 1;" v-if="subscriptionAccessType === 'FULL'">
      <div class="btn-group">
        <v-menu transition="slide-y-transition" location="top" contained>
          <template v-slot:activator="{ props: menuProps }">
            <v-tooltip location="top" open-delay="500" :disabled="menuProps['aria-expanded'] === 'true'">
              <template v-slot:activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="{ ...tooltipProps, ...menuProps }"
                  variant="tonal"
                  class="mr-2"
                  icon="mds:cloud-download"
                />
              </template>
              <span>Export / Import</span>
            </v-tooltip>
          </template>
          <v-list max-height="500">
            <v-list-subheader>Export / import options:</v-list-subheader>
            <v-tooltip location="right" :disabled="!hasWorkflowContent">
              <template v-slot:activator="{ props: importTooltipProps }">
                <div v-bind="importTooltipProps">
                  <v-list-item :disabled="hasWorkflowContent" @click="importFile">
                    <v-list-item-title>Import Workflow</v-list-item-title>
                  </v-list-item>
                </div>
              </template>
              <span>Clear the workflow before importing</span>
            </v-tooltip>
            <v-divider class="my-1" />
            <v-list-item :disabled="!workflow.domainEvents || !workflow.domainEvents.length" @click="exportCSV(workflow.id)">
              <v-list-item-title>Export CSV</v-list-item-title>
            </v-list-item>
            <v-list-item
              :disabled="!workflow.domainEvents || !workflow.domainEvents.length"
              @click="exportJson(workflow.id)"
              v-if="hasWorkflowContent"
            >
              <v-list-item-title>Export JSON</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportWorkflow" v-if="hasWorkflowContent">
              <v-list-item-title>Export Workflow</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="!workflow.domainEvents || !workflow.domainEvents.length" @click="exportToPdf()">
              <v-list-item-title>Export PDF</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportApplicon" v-if="workflow && workflow.canAppliconExport">
              <v-list-item-title>Export Applicon</v-list-item-title>
            </v-list-item>

            <workflow-export-cards-dialog />

            <workflow-export-azure-dev-ops-dialog />
          </v-list>
        </v-menu>
        <input
          data-cy="import-file-input"
          type="file"
          ref="importFileInput"
          accept=".json,.qlerify"
          style="display: none"
          @change="processImport"
        />

        <v-tooltip v-if="!$store.state.workflow.isViewOnlyEnabled" location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mds:settings"
              class="mr-2"
              @click="showPreferredDialog"
            />
          </template>
          <span>Workflow settings</span>
        </v-tooltip>

        <v-menu contained>
          <template v-slot:activator="{ props: menuProps }">
            <v-tooltip location="top" open-delay="500"
                       :disabled="menuProps['aria-expanded'] === 'true'">
              <template v-slot:activator="{ props: tooltipProps }">
                <v-btn
                  variant="tonal"
                  icon="mdi-information-outline"
                  v-bind="{ ...tooltipProps, ...menuProps }"
                >
                </v-btn>
              </template>
              <span>View the legend for the workflow</span>
            </v-tooltip>
          </template>

          <diagram-legend class="mt-4" />
        </v-menu>

        <v-dialog v-model="exportCsvDialog" v-on:keydown.stop.esc="closeExportCsvDialog">
          <v-card>
            <workflow-export-csv
              ref="exportCsv"
              :workflow="workflow"
              :show-done="showDone"
              @closeExportCsvDialog="closeExportCsvDialog"
            />
          </v-card>
        </v-dialog>

        <v-dialog v-model="exportJsonDialog" v-on:keydown.stop.esc="closeExportJsonDialog">
          <v-card>
            <workflow-export-json
              ref="exportJson"
              :workflow="workflow"
              @closeExportJsonDialog="closeExportJsonDialog"
            />
          </v-card>
        </v-dialog>
      </div>
    </div>

    <div
      class="floating-side-panel-button"
      :class="{'floating-side-panel-button-hidden': showSidePanel}"
    >
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="tonal"
            icon="mds:right_panel_open"
            :active="['cards', 'fields', 'fileUpload'].includes(sidePanelTab) && showSidePanel"
            @click="() => (['cards', 'fields', 'fileUpload'].includes(sidePanelTab) && showSidePanel) ? closeSidePanel() : openSidePanelTab()"
          />
        </template>
        <span>Open side panel</span>
      </v-tooltip>
    </div>

    <disclaimer-dialog
      ref="disclaimerDialog"
      @conditions-accepted="onConditionsAccepted()"
    />

    <workflow-settings-dialog
      :workflow="workflow"
      :open="workflowSettingsOpen"
      :tab-id="workflowSettingsTab"
      :disabled="workflowStatusCompleted"
      @close="closeWorkflowSettings"
    ></workflow-settings-dialog>
  </div>
</template>

<script>
import workflowApi from '@/api/workflowApi'
import WorkflowExportCsv from '@/components/WorkflowExportCsv'
import WorkflowExportJson from '@/components/WorkflowExportJson'
import WorkflowExportCardsDialog from '@/components/WorkflowExportCardsDialog'
import WorkflowExportAzureDevOpsDialog from '@/components/WorkflowExportAzureDevOpsDialog'
import WorkflowSettingsDialog from '@/components/WorkflowSettingsDialog'
import DiagramLegend from '@/components/DiagramLegend'
import DisclaimerDialog from '@/components/DisclaimerDialog'
import { workflowHelpers } from '@/mixins/workflowHelpers.js'
import AiAssistantButton from '@/components/AiAssistantButton.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { cloneAndPrepareSvg, exportDiagramAsPdf } from '@/utils/diagramPdfExport'
import { compress, decompress } from '@/utils/compression.js'
import { decryptTextWithPassword, encryptTextWithPassword } from '@/utils/encryption.js'
import { downloadFile, readFile } from '@/utils/fsUtils.js'
import { sanitizeFilename, sanitizeCardDescription } from '@/utils/sanitize'
import { validateWorkflowJson } from '@/helpers/workflowJsonValidator'
import { importBoundedContextJson } from '@/utils/workflowImport'
import { DomainModelRole } from '@/constants/cardTypes'

export default {
  mixins: [workflowHelpers],
  components: {
    AiAssistantButton,
    WorkflowExportCsv,
    WorkflowExportJson,
    WorkflowExportCardsDialog,
    WorkflowExportAzureDevOpsDialog,
    WorkflowSettingsDialog,
    DiagramLegend,
    DisclaimerDialog
  },
  props: ['previousVersionExists', 'versions', 'releases', 'showDone'],

  data () {
    return {
      exportCsvDialog: false,
      exportJsonDialog: false,
      aiDisclaimerPreference: localStorage.getItem(`showAiInfo-${this.$route.params.projectId}`) === 'true',
      isButtonOutOfView: false,
      filterMenuOpen: false,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      menuPositionX: 0,
      menuPositionY: 0
    }
  },
  methods: {
    ...mapActions({
      debounceUpdateWorkflow: 'debounceUpdateWorkflow/debounceUpdateWorkflow',
      openWorkflowSettings: 'workflow/openWorkflowSettings',
      closeWorkflowSettings: 'workflow/closeWorkflowSettings'
    }),
    undoDebounced (workflow) {
      this.debounceUpdateWorkflow({ fn: workflowApi.undo, args: [workflow] })
    },
    redoDebounced (workflow) {
      this.debounceUpdateWorkflow({ fn: workflowApi.redo, args: [workflow] })
    },
    openSidePanelTab (tab) {
      if (this.sidePanelTab === tab) {
        this.$store.commit('showSidePanel', !this.showSidePanel)
      } else {
        this.$store.commit('setSidePanelTab', tab ||
          (['cards', 'fields', 'fileUpload'].includes(this.sidePanelTab) ? this.sidePanelTab : 'cards'))
        this.$store.commit('showSidePanel', true)
      }
    },
    closeSidePanel () {
      this.$store.commit('showSidePanel', false)
    },
    exportCSV () {
      this.$nextTick(() => {
        this.exportCsvDialog = true
      })
    },
    exportJson () {
      this.$nextTick(() => {
        this.exportJsonDialog = true
        // this.$refs.exportCsv.refreshQuery()
      })
    },
    exportToPdf () {
      const flowChartOriginal = document.getElementById('flowchart')
      const workflowData = {
        svgLanesData: this.svgLanesData,
        roleDefinition: this.workflow.roleDefinition,
        hasGroups: this.hasGroups
      }

      const svgElement = cloneAndPrepareSvg(flowChartOriginal, workflowData)

      const workflowName = this.workflow.name
      exportDiagramAsPdf(svgElement, workflowName)
    },
    closeExportCsvDialog () {
      this.exportCsvDialog = false
    },
    closeExportJsonDialog () {
      this.exportJsonDialog = false
    },
    showPreferredDialog () {
      this.closeWorkflowSettings()
      this.$refs.disclaimerDialog.openDialog()
    },
    onConditionsAccepted () {
      this.openWorkflowSettings()
    },
    toggleReleaseFilter (item) {
      let releases = []
      if (item) {
        if (Array.isArray(this.showReleaseNo)) {
          releases = this.showReleaseNo
        }
        const value = item.value
        if (releases.includes(value)) {
          releases = releases.filter((releaseValue) => releaseValue !== value)
        } else {
          releases = [...releases, value]
        }
        // Set to null if empty array to match original behavior
        this.$store.commit('workflow/setShowReleaseNo', releases.length > 0 ? releases : null)
      }
    },
    async exportWorkflow () {
      // Get all bounded contexts and their objects
      if (this.workflow.boundedContexts) {
        await Promise.all(this.workflow.boundedContexts.map(async (bc) => {
          if (!this.boundedContexts[bc.id]) {
            return this.$store.dispatch('boundedContexts/getObjectsFromServer', { boundedContextId: bc.id })
          }
        }))
      }

      const workflowData = JSON.stringify({
        workflow: this.workflow,
        cardTypes: this.cardTypes,
        boundedContexts: this.boundedContexts
      })
      const compressedFileContent = compress(workflowData)
      const encryptedFileContent = await encryptTextWithPassword('qlerify', compressedFileContent)

      downloadFile(
        sanitizeFilename(this.workflow.name, 'qlerify'),
        'application/json',
        JSON.stringify(encryptedFileContent)
      )
    },
    importFile () {
      this.$refs.importFileInput.click()
    },
    processImport (event) {
      const file = event.target.files[0]
      if (!file) {
        console.error('No file selected.')
        return
      }

      const isQlerifyFile = file.name.endsWith('.qlerify')

      if (isQlerifyFile) {
        this.processQlerifyImport(file)
      } else {
        this.processJsonImport(file)
      }
    },
    processJsonImport (file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

      if (file.size > MAX_FILE_SIZE) {
        this.$store.commit('snackbar/showMessage', {
          content: `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 10 MB.`,
          timeout: 6000,
          color: 'red'
        }, { root: true })

        return
      }

      readFile(file, async (data) => {
        try {
          const parsed = JSON.parse(data)

          // Auto-detect spec export format: top-level boundedContext string + version number
          const isSpecFormat = typeof parsed.boundedContext === 'string' && typeof parsed.version === 'number'

          let workflow
          let cardTypes

          if (isSpecFormat) {
            const currentCardTypes = this.cardTypes || []
            const cardTypesMap = {}
            for (const ct of currentCardTypes) {
              if (ct.domainModelRole) cardTypesMap[ct.domainModelRole] = ct.id
              if (ct.title) cardTypesMap[`__title:${ct.title}`] = ct.id
            }
            const defaultCt = currentCardTypes.find((ct) => ct.defaultType)
            if (defaultCt) cardTypesMap.__default__ = defaultCt.id
            workflow = importBoundedContextJson(parsed, cardTypesMap)
            workflow.aiVersion = this.workflow.aiVersion
            cardTypes = currentCardTypes
          } else {
            const hasWrapper = 'workflow' in parsed
            workflow = hasWrapper ? parsed.workflow : parsed
            cardTypes = hasWrapper ? (parsed.cardTypes || []) : []
          }

          const validationError = validateWorkflowJson(workflow, cardTypes)
          if (validationError) {
            throw new Error(validationError)
          }

          const workflowData = { workflow, cardTypes }

          await this.uploadWorkflow(JSON.stringify(workflowData))
        } catch (error) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Invalid JSON file: ' + (error.message || 'Failed to parse file'),
            timeout: 6000,
            color: 'red'
          }, { root: true })
        } finally {
          this.$refs.importFileInput.value = ''
        }
      })
    },
    processQlerifyImport (file) {
      readFile(file, async (data) => {
        try {
          const decryptedFileContent = await decryptTextWithPassword('qlerify', JSON.parse(data))
          const decompressedData = decompress(decryptedFileContent)
          const { workflow, cardTypes } = JSON.parse(decompressedData)

          const workflowData = {
            workflow,
            cardTypes
          }

          await this.uploadWorkflow(JSON.stringify(workflowData))
        } catch (error) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Invalid .qlerify file: ' + (error.message || 'Failed to parse file'),
            timeout: 6000,
            color: 'red'
          }, { root: true })
        } finally {
          this.$refs.importFileInput.value = ''
        }
      })
    },
    async uploadWorkflow (workflowData) {
      this.$loadingState.start('Importing workflow...')
      await this.$store.commit('workflow/setLoadingUpdate', true)

      try {
        this.$loadingState.setMessage('Removing existing card types...')
        await this.deleteCardTypes(this.cardTypes)

        this.$loadingState.setMessage('Importing workflow...')
        await workflowApi.importWorkflow(this.workflow.id, this.workflow.projectId, workflowData)
        await this.$store.dispatch('cardTypes/listCardTypes', {
          workflowId: this.workflow.id
        })
      } catch (error) {
        this.$store.commit('snackbar/showMessage', {
          content: error.message || 'Failed to import workflow.',
          timeout: 6000,
          color: 'red'
        }, { root: true })
      } finally {
        this.$loadingState.stop()
        await this.$store.commit('workflow/setLoadingUpdate', false)
      }
    },
    exportApplicon () {
      const entities = (this.workflow.schemas || []).map((entity) => ({
        name: entity.name,
        attributes: entity.fields.map((field) => ({
          name: field.name,
          type: field.type || 'string',
          required: field.tags?.includes('REQUIRED') || false
        }))
      }))
      const commands = (this.workflow.domainEvents || []).flatMap((event) => {
        return (event.cards || [])
          .filter((card) => card.cardType?.domainModelRole === DomainModelRole.COMMAND)
          .map((card) => sanitizeCardDescription(card.description))
      })

      const data = { entities, commands }

      downloadFile('applicon.json', 'application/json', JSON.stringify(data, null, 2))
    },
    getTagValuesForCardType (cardTypeId) {
      const tagValues = new Set()

      // Find all cards with this cardTypeId in the workflow and collect their tags
      if (this.workflow?.domainEvents) {
        for (const event of this.workflow.domainEvents) {
          if (event.cards) {
            for (const card of event.cards) {
              if (card.cardTypeId === cardTypeId && card.tags && Array.isArray(card.tags)) {
                card.tags.forEach(tag => tagValues.add(tag))
              }
            }
          }
        }
      }

      return Array.from(tagValues).sort()
    },
    getTagsForCard (card) {
      // Get tags directly from the card object (stored in backend)
      return card.tags || []
    },
    toggleTagCardTypeFilter (tagValue) {
      let tags = []
      if (Array.isArray(this.showTagCardType)) {
        tags = this.showTagCardType
      }
      if (tags.includes(tagValue)) {
        tags = tags.filter((t) => t !== tagValue)
      } else {
        tags = [...tags, tagValue]
      }
      this.$store.commit('workflow/setShowTagCardType', tags.length > 0 ? tags : null)
    },
    clearFilters () {
      this.$store.commit('workflow/setShowReleaseNo', null)
      this.$store.commit('workflow/setShowTagCardType', null)
    },
    closeFilterMenu () {
      this.filterMenuOpen = false
    },
    handleDragStart (e) {
      if (!this.isButtonOutOfView) return
      // Don't start drag if clicking on close button
      if (e.target.closest('.filter-menu-close-btn')) {
        return
      }

      this.isDragging = true
      this.dragStartX = e.clientX - this.menuPositionX
      this.dragStartY = e.clientY - this.menuPositionY

      document.addEventListener('mousemove', this.handleDrag)
      document.addEventListener('mouseup', this.handleDragEnd)
      e.preventDefault()
      e.stopPropagation()
    },
    handleDrag (e) {
      if (!this.isDragging) return

      this.menuPositionX = e.clientX - this.dragStartX
      this.menuPositionY = e.clientY - this.dragStartY

      // Constrain to viewport
      const menuElement = this.$refs.filterMenuWrapper?.closest('.v-overlay__content')
      if (menuElement) {
        const maxX = window.innerWidth - menuElement.offsetWidth
        const maxY = window.innerHeight - menuElement.offsetHeight

        this.menuPositionX = Math.max(0, Math.min(this.menuPositionX, maxX))
        this.menuPositionY = Math.max(0, Math.min(this.menuPositionY, maxY))

        menuElement.style.left = `${this.menuPositionX}px`
        menuElement.style.top = `${this.menuPositionY}px`
        menuElement.style.transform = 'none'
      }
    },
    handleDragEnd () {
      this.isDragging = false
      document.removeEventListener('mousemove', this.handleDrag)
      document.removeEventListener('mouseup', this.handleDragEnd)
    },
    getReleaseCount (releaseValue) {
      if (!this.workflow?.domainEvents) return 0

      const tagCardTypeId = this.tagCardType?.id || null

      // Create a map of cardTypeId to showInBacklog
      const typeMap = new Map((this.cardTypes ?? []).map((type) => [type.id, type.showInBacklog]))

      // Count events (not cards) that match the criteria
      let count = 0
      for (const event of this.workflow.domainEvents) {
        if (!event.cards) continue

        // Check if event has cards with this release number AND showInBacklog is true
        const hasRelease = event.cards.some((card) =>
          card.releaseNo === releaseValue && typeMap.get(card.cardTypeId)
        )
        if (!hasRelease) continue

        // Check tag filter (but not release filter from same column)
        let matchesTagCardType = !this.showTagCardType || !this.showTagCardType.length || !tagCardTypeId

        if (this.showTagCardType && this.showTagCardType.length > 0 && tagCardTypeId) {
          matchesTagCardType = event.cards.some((card) => {
            if (card.cardTypeId === tagCardTypeId) {
              const cardTags = this.getTagsForCard(card)
              return cardTags.some((tag) => this.showTagCardType.includes(tag))
            }
            return false
          })
        }

        if (matchesTagCardType) {
          count++
        }
      }

      return count
    },
    getTagCardTypeCount (tagValue) {
      if (!this.tagCardType || !this.workflow?.domainEvents) return 0

      const tagCardTypeId = this.tagCardType.id

      // Create a map of cardTypeId to showInBacklog
      const typeMap = new Map((this.cardTypes ?? []).map((type) => [type.id, type.showInBacklog]))

      // Count events (not cards) that match the criteria
      let count = 0
      for (const event of this.workflow.domainEvents) {
        if (!event.cards) continue

        // Check if event has cards with this tag in Tag Card Type
        const hasTag = event.cards.some((card) => {
          if (card.cardTypeId === tagCardTypeId) {
            const cardTags = this.getTagsForCard(card)
            return cardTags.includes(tagValue)
          }
          return false
        })
        if (!hasTag) continue

        // Check release filter (but not Tag Card Type filter from same column)
        // Only count cards with showInBacklog enabled when checking release filter
        let matchesRelease = !this.showReleaseNo || !this.showReleaseNo.length
        if (this.showReleaseNo && this.showReleaseNo.length > 0) {
          matchesRelease = event.cards.some((card) =>
            this.showReleaseNo.includes(card.releaseNo) && typeMap.get(card.cardTypeId)
          )
        }

        if (matchesRelease) {
          count++
        }
      }

      return count
    }
  },
  computed: {
    ...mapGetters({
      project: 'projectMemberships/activeProject',
      svgLanesData: 'svgGraphData/svgLanesData'
    }),
    ...mapState({
      user: (state) => state.user.user,
      workflow: (state) => state.workflow.workflow,
      cardTypes: (state) => state.cardTypes.cardTypes,
      boundedContexts: (state) => state.boundedContexts.boundedContexts,
      showReleaseNo: (state) => state.workflow.showReleaseNo,
      showTagCardType: (state) => state.workflow.showTagCardType,
      sidePanelTab: (state) => state.sidePanelTab,
      showSidePanel: (state) => state.showSidePanel,
      subscriptionAccessType: (state) => state.payments.subscriptionAccessType,
      workflowSettingsOpen: (state) => state.workflow.workflowSettingsOpen,
      workflowSettingsTab: (state) => state.workflow.workflowSettingsTab
    }),
    changeNoteCurrent () {
      if (!this.workflow?.sameAsVersion) {
        return this.workflow?.updateNote
      } else {
        const index = this.versions?.findIndex((element) => element.version === this.workflow.sameAsVersion)
        return this.versions?.[index]?.updateNote
      }
    },
    changeNoteRedo () {
      if (!this.workflow?.sameAsVersion) {
        return {
          userRealName: '',
          action: '',
          target: ''
        }
      } else {
        const index = this.versions?.findIndex((element) => element.version === (this.workflow.sameAsVersion + 1))
        return this.versions?.[index]?.updateNote || ''
      }
    },
    laterVersionExists () {
      return !!(this.workflow?.sameAsVersion &&
        this.versions?.findIndex((element) => element.version === (this.workflow.sameAsVersion + 2)) >= 0)
    },
    hasWorkflowContent () {
      return !!(this.workflow && this.workflow.lanes && this.workflow.lanes.length)
    },
    hasGroups () {
      return this.workflow?.groups && this.workflow.groups.length
    },
    tagCardType () {
      if (!this.cardTypes) return null
      const tagCardTypes = this.cardTypes.filter((ct) => ct.domainModelRole === DomainModelRole.TAG)
      return tagCardTypes[0] || null
    },
    tagCardTypeValues () {
      if (!this.tagCardType || !this.workflow?.domainEvents) return []
      return this.getTagValuesForCardType(this.tagCardType.id)
    },
    activeFilterCount () {
      let count = 0
      if (this.showReleaseNo?.length) count += this.showReleaseNo.length
      if (this.showTagCardType?.length) count += this.showTagCardType.length
      return count
    }
  },
  watch: {
    releases (newVal) {
      if (!this.showReleaseNo) {
        return
      }
      // filter out selected releases that no longer exists
      const showReleaseNo = this.showReleaseNo.filter((release) => {
        return newVal.findIndex((item) => item.value === release) !== -1
      })
      this.$store.commit('workflow/setShowReleaseNo', showReleaseNo)
    },
    'workflow.id' () {
      // Reset filters when workflow changes
      this.$store.commit('workflow/setShowReleaseNo', null)
      this.$store.commit('workflow/setShowTagCardType', null)
    }
  },
  async mounted () {
    // Next line prevents the release selector from being populated by data from different workflow
    // when the user navigates to workflow page
    this.$store.commit('workflow/setShowReleaseNo', null)
    this.$store.commit('workflow/setShowTagCardType', null)

    // Add scroll listener to detect when filter button is out of view
    this.handleScroll = () => {
      if (this.$refs.filterButtonRef) {
        const rect = this.$refs.filterButtonRef.getBoundingClientRect()
        // Check if button is completely out of viewport (above or below)
        const isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight
        const wasOutOfView = this.isButtonOutOfView
        this.isButtonOutOfView = isOutOfView

        // Initialize menu position when it first goes out of view
        if (isOutOfView && !wasOutOfView && this.filterMenuOpen) {
          this.$nextTick(() => {
            const menuElement = this.$refs.filterMenuWrapper?.closest('.v-overlay__content')
            if (menuElement) {
              const rect = menuElement.getBoundingClientRect()
              this.menuPositionX = rect.left
              this.menuPositionY = rect.top
            }
          })
        }
      }
    }
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleScroll, { passive: true })
    // Initial check
    this.$nextTick(() => {
      this.handleScroll()
    })
  },
  beforeUnmount () {
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll)
      window.removeEventListener('resize', this.handleScroll)
    }
    this.handleDragEnd()
  }
}
</script>

<style scoped>
.btn-group {
  display: flex;
  padding: 4px 12px;
  border-radius: 8px;
  background-color: var(--light-1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
}

.flip-icon-horizontal {
  transform: scaleX(-1);
}

.hasSelected {
  background-color: var(--light-2) !important;
}

.filter-menu-wrapper {
  display: flex;
  flex-direction: column;
  box-shadow: 8px 4px 24px 0px rgba(0, 0, 0, 0.24) !important;
  border-radius: 10px;
  overflow: hidden;
  /* Prevent flickering during scroll */
  will-change: transform;
  transform: translateZ(0);
  /* Isolate rendering to prevent layout shifts */
  contain: layout style paint;
  position: relative;
}

.filter-menu-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px 10px 0 0;
  cursor: move;
  user-select: none;
}

.filter-menu-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  pointer-events: none;
}

.filter-menu-drag-handle .v-icon {
  color: rgba(0, 0, 0, 0.4);
}

.filter-menu-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.filter-menu-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.filter-menu-close-btn .v-icon {
  color: rgba(0, 0, 0, 0.6);
}

.filter-menu-container {
  display: flex;
  flex-direction: row;
  gap: 0;
}

.filter-menu-wrapper.has-header .filter-menu-container {
  margin-top: 32px;
}

.filter-column {
  min-width: 200px;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.filter-column:last-child {
  border-right: none;
}

.filter-menu-footer {
  padding: 8px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: var(--light-1);
}

:deep(.filter-column .v-list-item.releaseFilter.active) {
  background-color: var(--light-3, #E5E3E1) !important;
}

:deep(.filter-column .v-list-item.releaseFilter.active .v-list-item-title) {
  font-weight: 500;
}

/* Prevent menu flickering during scroll */
:deep(.v-menu__content) {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
  /* Disable transitions to prevent flickering */
  transition: none !important;
}

/* Prevent menu overlay from animating */
:deep(.v-overlay__content) {
  transition: none !important;
}

/* Make menu draggable when button is out of view */
:deep(.filter-menu-content.v-overlay__content) {
  position: fixed !important;
}

:deep(.filter-menu-content .filter-menu-wrapper.draggable) {
  cursor: move;
}

:deep(.floating-side-panel-button) {
  right: 0;
  top: 108px;
  z-index: 1;
  position: fixed;
  overflow: hidden;
  border-radius: 8px 0 0 8px;
  transition: 0.2s 0.1s right ease;
  background-color: var(--blue-2, #C3DAF6);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);

  .v-btn {
    width: 40px !important;
    height: 40px !important;
    background-color: var(--blue-2, #C3DAF6);
  }

  &.floating-side-panel-button-hidden {
    right: -100%;
  }
}
</style>
